import { resolveWithCompanyDefault } from "@/lib/utils/resolve-with-default";

export const CORE_ANCHOR_IDS: ReadonlySet<string> = new Set(["home"]);

export const REORDERABLE_SECTION_IDS: readonly string[] = ["content", "workspace"];

export interface SideMenuPreferences {
  order?: string[];
  hidden?: string[];
  sectionOrder?: string[];
  sectionHidden?: string[];
}

export const MENU_ITEM_LABEL_KEYS: Record<string, string> = {
  home: "MENU.HOME",
  dashboards: "MENU.DASHBOARDS",
  explore: "MENU.ANALYZE_DATA",
  "meeting-assistant": "MENU.MEETING_ASSISTANT",
  chat: "MENU.CHAT_HISTORY",
  favorites: "MENU.FAVORITES",
  recorder: "MENU.SURVEYS",
  "embed-media": "MENU.MEDIA",
  clips: "MENU.CLIPS",
  "knowledge-base": "MENU.KNOWLEDGE_BASE",
  automations: "MENU.AUTOMATIONS",
  integrations: "MENU.INTEGRATIONS",
  "team-manage": "MENU.TEAM",
  "developers-manage": "MENU.DEVELOPERS",
  "ai-assistant": "AI.AI_ASSISTANT",
  fields: "PROFILE.FIELDS",
  labels: "PROFILE.LABELS_TITLE",
  "agents-list": "MENU.AGENTS",
  "agents-conversations": "MENU.CONVERSATIONS",
  "agents-phone-numbers": "MENU.PHONE_NUMBERS",
};

export const SECTION_LABEL_KEYS: Record<string, string> = {
  content: "MENU.CONTENT",
  workspace: "MENU.WORKSPACE",
  agents: "MENU.AGENTS",
};

export function mergeSideMenuWithCompanyDefault(
  userPref: SideMenuPreferences | undefined,
  companyDefault: SideMenuPreferences | undefined,
): SideMenuPreferences | undefined {
  if (!companyDefault) return userPref;
  return {
    order: resolveWithCompanyDefault(userPref?.order, companyDefault.order, undefined),
    hidden: resolveWithCompanyDefault(userPref?.hidden, companyDefault.hidden, undefined),
    sectionOrder: resolveWithCompanyDefault(userPref?.sectionOrder, companyDefault.sectionOrder, undefined),
    sectionHidden: resolveWithCompanyDefault(userPref?.sectionHidden, companyDefault.sectionHidden, undefined),
  };
}

export function applyOrderPreference(
  baseOrder: string[],
  order: string[] | undefined,
  knownIds: Iterable<string>,
): string[] {
  const known = knownIds instanceof Set ? knownIds : new Set(knownIds);
  const seen = new Set<string>();
  const ordered: string[] = [];

  const place = (id: string) => {
    if (!known.has(id) || seen.has(id)) return;
    seen.add(id);
    ordered.push(id);
  };

  for (const id of order ?? []) place(id);
  for (const id of baseOrder) place(id);

  return ordered;
}

export function resolveSideMenuOrder(
  baseOrder: string[],
  pref: SideMenuPreferences | undefined,
  knownIds: Iterable<string>,
): string[] {
  const hidden = new Set(pref?.hidden ?? []);
  return applyOrderPreference(baseOrder, pref?.order, knownIds).filter(
    (id) => !hidden.has(id) || CORE_ANCHOR_IDS.has(id),
  );
}

export function resolveVisibleSections(
  pref: SideMenuPreferences | undefined,
): string[] {
  const hidden = new Set(pref?.sectionHidden ?? []);
  return applyOrderPreference(
    [...REORDERABLE_SECTION_IDS],
    pref?.sectionOrder,
    REORDERABLE_SECTION_IDS,
  ).filter((id) => !hidden.has(id));
}

export function resolveSectionOrder(
  baseSectionOrder: string[],
  pref: SideMenuPreferences | undefined,
): string[] {
  const reorderable = new Set(REORDERABLE_SECTION_IDS);
  const baseReorderable = baseSectionOrder.filter((id) => reorderable.has(id));
  const baseReorderableSet = new Set(baseReorderable);
  const queue = resolveVisibleSections(pref).filter((id) => baseReorderableSet.has(id));

  let cursor = 0;
  const result: string[] = [];
  for (const id of baseSectionOrder) {
    if (reorderable.has(id)) {
      if (cursor < queue.length) result.push(queue[cursor++]);
    } else {
      result.push(id);
    }
  }
  return result;
}
